import React, { useEffect, useState } from "react";
import { onSnapshot, collection, doc } from "firebase/firestore";
import Context from "../../Context";
import { useToast } from "../../components/ui";
import { FiGift } from "react-icons/fi";
import { roundDateToDate } from "../../util";
import { Header } from "../../components/modules";
import { FaTimes, FaCheckCircle } from "react-icons/fa";
import { Spinner } from "reactstrap";

// ============================================
// Constants
// ============================================
const INITIAL_PRIZES = {
  first: {
    numbers: [],
    count: 1,
    length: 6,
    digitType: "number",
    text: "รางวัลที่ 1",
    prizeAmount: 6000000,
    height: "h-8",
  },
  nearBy: {
    numbers: [],
    count: 2,
    length: 6,
    digitType: "number",
    text: "รางวัลข้างเคียงรางวัลที่ 1",
    prizeAmount: 100000,
    height: "h-8",
  },
  second: {
    numbers: [],
    count: 5,
    length: 6,
    digitType: "number",
    text: "รางวัลที่ 2",
    prizeAmount: 200000,
    height: "h-8",
  },
  firstThree: {
    numbers: [],
    count: 2,
    length: 3,
    digitType: "firstThree",
    text: "เลขหน้า 3 ตัว",
    prizeAmount: 4000,
    height: "h-8",
  },
  lastThree: {
    numbers: [],
    count: 2,
    length: 3,
    digitType: "lastThree",
    text: "เลขท้าย 3 ตัว",
    prizeAmount: 4000,
    height: "h-8",
  },
  lastTwo: {
    numbers: [],
    count: 1,
    length: 2,
    digitType: "lastTwo",
    text: "เลขท้าย 2 ตัว",
    prizeAmount: 2000,
    height: "h-8",
  },
  third: {
    numbers: [],
    count: 10,
    length: 6,
    digitType: "number",
    text: "รางวัลที่ 3",
    prizeAmount: 80000,
    height: "h-96",
  },
  forth: {
    numbers: [],
    count: 50,
    length: 6,
    digitType: "number",
    text: "รางวัลที่ 4",
    prizeAmount: 40000,
    height: "h-96",
  },
  fith: {
    numbers: [],
    count: 100,
    length: 6,
    digitType: "number",
    text: "รางวัลที่ 5",
    prizeAmount: 20000,
    height: "h-96",
  },
};

const PRIZE_STATUS = {
  PENDING: "pending",
  MATCHED: "matched",
  FAILED: "failed",
};

// ============================================
// Helper
// ============================================
const getRegexForType = (type) => {
  if (["lastThree", "firstThree"].includes(type)) return /.{1,3}/g;
  if (type === "lastTwo") return /.{1,2}/g;
  return /.{1,6}/g;
};

const parseNumbers = (text, type) =>
  text.replaceAll(" ", "").match(getRegexForType(type)) || [];

const ToastTitle = ({ message }) => (
  <div className="flex gap-2 items-center">
    <FaTimes /> {message}
  </div>
);

// ============================================
// Main Component
// ============================================
export const MakeReward = () => {
  const { toast } = useToast();
  const { callApi, db, configs } = Context();
  const [getAllWinnerOrdersPercent, setGetAllWinnerOrdersPercent] =
    useState(-1);
  const [prizes, setPrizes] = useState(INITIAL_PRIZES);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getRoundPrize();

    // Fix: subscribe and return the unsubscribe function directly
    const makingPrizeOrders = doc(
      collection(db, "Status"),
      "makingPrizeOrders",
    );
    const unsubscribe = onSnapshot(makingPrizeOrders, (snapshot) => {
      const { find, marked } = snapshot.get("markingOrders");
      setGetAllWinnerOrdersPercent((prev) => {
        if (prev === -1) return 0;
        return find === 0 ? 0 : Math.floor((marked / find) * 100);
      });
    });

    return unsubscribe;
  }, []);

  const getRoundPrize = async () => {
    const result = await callApi("prizes-getRoundPrizes", {
      roundDate: configs.roundDate,
    });
    if (!result) return;
    // Fix: immutable update — create new object instead of mutating old
    setPrizes((old) => {
      const updated = { ...old };
      Object.keys(updated).forEach((key) => {
        updated[key] = { ...updated[key], ...result[key] };
      });
      return updated;
    });
  };

  const getAllWinnerOrders = async () => {
    if (!window.confirm("ท่านต้องการตรวจรางวัลในทุกคำสั่งซื้อ\nใช่ หรือ ไม่"))
      return;
    setLoading(true);
    await callApi("prizes-getAllWinnerOrders");
    setLoading(false);
  };

  // Fix: added await so loading state actually reflects the API call
  const getBookPrizes = async () => {
    setLoading(true);
    await callApi("prizes-getAllWinnerLotteries");
    setLoading(false);
  };

  const validate = () => {
    return Object.entries(prizes).every(([, value]) => {
      const { numbers, count, length, text } = value;

      if (!numbers) {
        toast({
          className: "bg-[#c15b78]",
          title: <ToastTitle message={`${text} ข้อมูลไม่ถูกต้อง`} />,
        });
        return false;
      }

      const invalidLength = numbers.find((n) => n.length !== length);
      if (invalidLength) {
        // Fix: use value.length instead of hardcoded "6 หลัก"
        toast({
          className: "bg-[#c15b78]",
          title: (
            <ToastTitle
              message={`${text} มีเลขบางตัวไม่ครบ ${length} หลัก (${invalidLength})`}
            />
          ),
        });
        return false;
      }

      const notNumber = numbers.find((n) => isNaN(n));
      if (notNumber) {
        toast({
          className: "bg-[#c15b78]",
          title: (
            <ToastTitle
              message={`${text} มีเลขรางวัลบางตัวไม่ใช่ตัวเลข (${notNumber})`}
            />
          ),
        });
        return false;
      }

      if (numbers.length !== count) {
        toast({
          className: "bg-[#c15b78]",
          title: (
            <ToastTitle
              message={`${text} มีเลขรางวัลไม่ครบ (${numbers.length}/${count})`}
            />
          ),
        });
        return false;
      }

      return true;
    });
  };

  const submit = async () => {
    if (!validate()) return;
    setLoading(true);
    const res = Object.entries(prizes).reduce((acc, [key, value]) => {
      acc[key] = {
        numbers: value.numbers,
        prizeAmount: value.prizeAmount,
        digitType: value.digitType,
      };
      return acc;
    }, {});
    const status = await callApi(
      "prizes-editAll",
      { id: configs.roundDate, value: res },
      { notShowLoading: true },
    );
    toast({
      className: status ? "bg-green" : "bg-failed",
      title: (
        <div className="flex gap-2 items-center">
          {status ? <FaCheckCircle /> : <FaTimes />}{" "}
          {status ? "บันทึกสำเร็จ" : "บันทึกไม่สำเร็จ"}
        </div>
      ),
    });
    setLoading(false);
  };

  return (
    <div
      className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto"
      id="main"
    >
      <Header
        header="Rewards"
        subHeader="สร้างรางวัล"
        icon={<FiGift className="text-blue" />}
      />

      <div className="relative my-8 bg-white rounded-md shadow-md">
        {loading && (
          <div className="absolute w-full col-span-3 border-r border-[#E5E7E9] z-50 h-full bg-[rgb(255,255,255,0.6)] flex items-center justify-center">
            <Spinner className="w-4 h-4" />
          </div>
        )}
        <div className="flex items-center p-3 border-b border-[#E5E7E9] justify-between">
          <p className="font-semibold text-xl">
            รางวัลงวด {roundDateToDate(configs.roundDate)}
          </p>
        </div>
        <div className="p-3 grid grid-cols-3 gap-3">
          {Object.entries(prizes).map(([key, value]) => (
            <PrizeBox
              key={key}
              type={key}
              value={value}
              setPrizes={setPrizes}
            />
          ))}
        </div>
        <div className="flex p-3 gap-3 h-9">
          <button
            className="px-3 bg-blue hover:opacity-60 duration-150 text-white font-semibold rounded-md disabled:bg-gray-300 disabled:hover:opacity-100 disabled:cursor-not-allowed"
            onClick={submit}
          >
            บันทึกรางวัล
          </button>
          <button
            className="px-3 bg-[#4DA1A9] hover:opacity-60 duration-150 text-white font-semibold rounded-md disabled:bg-gray-300 disabled:hover:opacity-100 disabled:cursor-not-allowed"
            onClick={getAllWinnerOrders}
            disabled={getAllWinnerOrdersPercent > 0 || loading}
          >
            ตรวจรางวัลในทุกคำสั่งซื้อ ({getAllWinnerOrdersPercent}%)
          </button>
          <button
            className="px-3 bg-[#FAB12F] hover:opacity-60 duration-150 text-white font-semibold rounded-md disabled:bg-gray-300 disabled:hover:opacity-100 disabled:cursor-not-allowed"
            onClick={getBookPrizes}
          >
            หารางวัลในเล่ม
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Subcomponent: PrizeBox
// ============================================
const PrizeBox = ({ type, value, setPrizes }) => {
  const [status, setStatus] = useState(PRIZE_STATUS.PENDING);
  const [numbers, setNumbers] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    setNumbers(value.numbers.join(" "));
    setConfirm("");
    setStatus(PRIZE_STATUS.PENDING);
  }, [value.numbers]);

  const handleNumbersChange = (e) => {
    const raw = e.target.value;
    if (!raw) {
      setNumbers("");
      setConfirm("");
      setStatus(PRIZE_STATUS.PENDING);
      // Fix: immutable update
      setPrizes((old) => ({ ...old, [type]: { ...old[type], numbers: [] } }));
      return;
    }
    const arrayNumber = parseNumbers(raw, type);
    setNumbers(arrayNumber.join(" "));
    setConfirm("");
    // Fix: immutable update
    setPrizes((old) => ({
      ...old,
      [type]: { ...old[type], numbers: arrayNumber },
    }));
    // Fix: use || instead of && — FAILED if either condition is bad
    const hasWrongLength = arrayNumber.some((n) => n.length !== value.length);
    const overCount = arrayNumber.length > value.count;
    setStatus(
      hasWrongLength || overCount ? PRIZE_STATUS.FAILED : PRIZE_STATUS.PENDING,
    );
  };

  const handleConfirmChange = (e) => {
    const arrayNumber = parseNumbers(e.target.value, type);
    setConfirm(arrayNumber.join(" "));

    const allMatch =
      arrayNumber.length === value.numbers.length &&
      arrayNumber.every((n) => value.numbers.includes(n));
    const allCorrectLength = arrayNumber.every(
      (n) => n.length === value.length,
    );
    const correctCount = arrayNumber.length === value.count;

    setStatus(
      allMatch && allCorrectLength && correctCount
        ? PRIZE_STATUS.MATCHED
        : PRIZE_STATUS.FAILED,
    );
  };

  const boxClass = `rounded-md shadow-sm p-2 ${value.height} font-semibold duration-150 ${
    status === PRIZE_STATUS.MATCHED
      ? "bg-green text-white"
      : status === PRIZE_STATUS.FAILED
        ? "bg-failed text-white"
        : "border border-gray-400"
  }`;

  return (
    <div>
      <p className="font-semibold">
        {value.text}{" "}
        <span className="font-medium text-sm">
          ({value.numbers.length}/{value.count})
        </span>
      </p>
      <div className="grid grid-cols-2 gap-2">
        <textarea
          className={boxClass}
          value={numbers}
          onChange={handleNumbersChange}
        />
        <textarea
          className={`${boxClass} placeholder:font-medium`}
          disabled={value.numbers.length === 0}
          value={confirm}
          onChange={handleConfirmChange}
        />
      </div>
    </div>
  );
};
